// Drives a membership verify/check-in after a scan resolves to a membership
// card (SLICE-MEMBERSHIP-PASS). On mount it POSTs the scanned token to the
// matching check-in endpoint (owner sends a branch_id; staff's branch is
// derived server-side from the session) and renders the read-only verify
// panel. Used by both the owner DashboardPage and the staff home.
//
// The check-in is fired exactly once per mount (ref guard against React 18
// StrictMode double-invoke), mirroring the RecoverPassModal pattern.

import { useEffect, useRef, useState } from 'react'

import { ownerApi, OwnerApiError } from '../../owner/ownerApi.js'
import { errorMessageFor } from '../../owner/errorMessages.js'
import { MembershipVerifyPanel } from './MembershipVerifyPanel.js'
import type { MembershipCheckinResponse } from './membershipTypes.js'

export function MembershipScanController({
  cardId,
  rawToken,
  /** Owner path: the active branch id (membership check-in needs it). Staff
   *  path omits it — the staff endpoint derives the branch from the session. */
  branchId,
  /** Which check-in endpoint to hit. */
  endpoint,
  onDone,
}: {
  cardId: string
  rawToken: string
  branchId?: string | null
  endpoint: 'owner' | 'staff'
  onDone: () => void
}) {
  const [data, setData] = useState<MembershipCheckinResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true

    const path =
      endpoint === 'staff'
        ? `/staff/cards/${encodeURIComponent(cardId)}/membership/checkin`
        : `/cards/${encodeURIComponent(cardId)}/membership/checkin`
    const body: Record<string, unknown> = { stamp_token: rawToken }
    if (endpoint === 'owner') body['branch_id'] = branchId

    ownerApi
      .post<MembershipCheckinResponse>(path, body)
      .then((res) => setData(res))
      .catch((err) => {
        setError(
          err instanceof OwnerApiError
            ? errorMessageFor(err.code)
            : 'Could not verify this membership. Try scanning again.',
        )
      })
      .finally(() => setLoading(false))
    // One-shot on mount; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MembershipVerifyPanel
      data={data}
      loading={loading}
      error={error}
      onDone={onDone}
    />
  )
}
